import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ChevronDown, ArrowRight } from "lucide-react";
import { SERVICES } from "../data/content";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services", hasMenu: true },
  { to: "/home-care-agency", label: "Home Care" },
  { to: "/healthcare-staffing", label: "Staffing" },
  { to: "/locations", label: "Locations" },
  { to: "/blog", label: "Journal" },
  { to: "/about", label: "About" },
  { to: "/careers", label: "Careers" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
  }, [pathname]);

  return (
    <header data-testid="site-nav">
      {/* Thin top bar (matches 1st-nation style) */}
      <div className="bg-dark text-bg text-xs" data-testid="nav-topbar">
        <div className="site-container flex items-center justify-between py-2">
          <span className="tracking-wide">
            Nationwide · Bonded &amp; insured · Licensed nurses on staff
          </span>
          <a
            href="tel:8073558600"
            className="inline-flex items-center gap-1.5 text-accent hover:text-bg"
            data-testid="nav-topbar-phone"
          >
            <Phone className="w-3.5 h-3.5" strokeWidth={1.75} /> 807-355-8600
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className="bg-bg border-b border-ink/10 sticky top-0 z-40">
        <div className="site-container flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <LogoMark />
            <span className="font-serif text-2xl leading-none">
              Xtreme <span className="italic text-accent">Care</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.slice(0, -1).map((item) =>
              item.hasMenu ? (
                <div
                  key={item.to}
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                  className="relative"
                >
                  <NavLink
                    to={item.to}
                    data-testid={`nav-link-${item.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `inline-flex items-center gap-1 px-3.5 py-2 text-sm font-medium transition-colors ${
                        isActive ? "text-accent" : "text-ink/80 hover:text-accent"
                      }`
                    }
                  >
                    {item.label}
                    <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />
                  </NavLink>
                  <AnimatePresence>
                    {servicesOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.18 }}
                        className="absolute left-1/2 -translate-x-1/2 top-full pt-2"
                        data-testid="services-mega-menu"
                      >
                        <div className="bg-bg border border-ink/10 shadow-xl rounded-md w-[640px] max-w-[calc(100vw-2rem)] p-3 grid grid-cols-2 gap-1">
                          {SERVICES.map((s) => (
                            <Link
                              key={s.slug}
                              to={`/services/${s.slug}`}
                              className="flex items-center justify-between px-3 py-2 rounded hover:bg-surface/70 group"
                              data-testid={`mega-service-${s.slug}`}
                            >
                              <span className="font-serif text-base leading-tight">
                                {s.title}
                              </span>
                              <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-70 group-hover:translate-x-0.5 transition-all text-accent" strokeWidth={2} />
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-link-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-3.5 py-2 text-sm font-medium transition-colors ${
                      isActive ? "text-accent" : "text-ink/80 hover:text-accent"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <NavLink
              to="/careers"
              className={({ isActive }) =>
                `text-sm font-medium ${isActive ? "text-accent" : "text-ink/80 hover:text-accent"}`
              }
              data-testid="nav-link-careers"
            >
              Careers
            </NavLink>
            <Link to="/contact" className="btn btn-primary" data-testid="nav-cta">
              Request Care
              <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
          </div>

          <button
            className="lg:hidden p-2 -mr-2"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="nav-mobile-toggle"
          >
            <Menu className="w-6 h-6" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3, ease: [0.65, 0, 0.35, 1] }}
            className="fixed inset-0 z-[70] bg-dark text-bg flex flex-col p-6"
            data-testid="mobile-drawer"
          >
            <div className="flex items-center justify-between">
              <span className="font-serif text-2xl">Xtreme <span className="italic text-accent">Care</span></span>
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="nav-mobile-close">
                <X className="w-6 h-6" strokeWidth={1.75} />
              </button>
            </div>
            <div className="flex flex-col mt-10 gap-1">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className="font-serif text-3xl py-2.5 border-b border-bg/10"
                  data-testid={`mobile-link-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </NavLink>
              ))}
              <Link to="/contact" className="mt-6 btn btn-primary self-start" data-testid="mobile-link-contact">
                Request Care <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Link>
            </div>
            <div className="mt-auto text-sm opacity-70">
              admin@xtremecare.ca · 807-355-8600
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function LogoMark() {
  // Simple sun / care mark, terracotta accent — matches 1st Nation warmth
  return (
    <svg width="34" height="34" viewBox="0 0 34 34" fill="none" aria-hidden>
      <circle cx="17" cy="17" r="6.5" fill="#C69749" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
        <line
          key={deg}
          x1="17"
          y1="17"
          x2="17"
          y2="4"
          stroke="#C69749"
          strokeWidth="1.5"
          strokeLinecap="round"
          transform={`rotate(${deg} 17 17)`}
        />
      ))}
    </svg>
  );
}
